import {
  Badge,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { CitySelect } from "../../components/CitySelect";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { ContentCard, EmptyState, FilterBar, ListScrollSentinel, LoadingOverlay, PageHeader, useInfinitePage } from "../../components/ui";
import { jobsApi } from "../../lib/api";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { estimateFeePaise, estimateSharePaise, formatCities, formatPaise, formatPaiseRange, SEGMENT_LABELS } from "../../lib/format";
import { maskedCompanyName } from "../../lib/kyc";
import { NAV_ICONS } from "../../lib/navCopy";
import { usePublicSegmentCatalog } from "../../lib/useSegmentCatalog";
import { axioTokens } from "../../theme";
import type { Job, Segment } from "../../types/api";

function sourcingLabel(job: Job): string {
  const fee = estimateFeePaise(job);
  const share = estimateSharePaise(fee, job.sourcing_fee_pct);
  if (!share) {
    if (job.sourcing_fee_pct != null) return `${job.sourcing_fee_pct}% of placement fee`;
    return "Sourcing share TBA";
  }
  return formatPaiseRange(share.min, share.max);
}

function experienceLabel(job: Job): string | null {
  const min = job.experience_min;
  const max = job.experience_max;
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}–${max} yrs`;
  if (min != null) return `${min}+ yrs`;
  return `Up to ${max} yrs`;
}

function AffiliateJobCard({ job }: { job: Job }) {
  const fee = estimateFeePaise(job);
  const share = estimateSharePaise(fee, job.sourcing_fee_pct);
  const openingsLeft = job.openings_total - job.openings_filled;
  const exp = experienceLabel(job);

  return (
    <ContentCard h="100%" padding="xl" style={{ display: "flex", flexDirection: "column" }}>
      <Stack gap="lg" style={{ flex: "var(--axio-number-one)" }}>
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Badge color="gray" variant="outline" size="sm">
            {SEGMENT_LABELS[job.segment] ?? job.segment}
          </Badge>
          {job.priority === "urgent" && (
            <Badge color="red" variant="outline" size="sm">
              Urgent
            </Badge>
          )}
        </Group>

        <Stack gap="var(--axio-unit-6)">
          <Text
            fw="var(--axio-font-weight-bold)"
            size="xl"
            lh="var(--axio-line-height-compact)"
            lineClamp={2}
            style={{ fontFamily: axioTokens.fontHead, letterSpacing: "var(--axio-letter-spacing-tight)" }}
          >
            {job.title}
          </Text>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {maskedCompanyName(job)}
          </Text>
          {job.description && (
            <Text size="sm" c="dimmed" lineClamp={2} mt="var(--axio-unit-2)">
              {job.description}
            </Text>
          )}
        </Stack>

        <Stack gap="var(--axio-unit-6)">
          <Text size="sm">
            {formatCities(job.cities)}
            {job.is_remote ? " · Remote OK" : ""}
          </Text>
          <Text size="sm" c="dimmed" className="tabular">
            {openingsLeft} of {job.openings_total} openings left
            {exp ? ` · ${exp}` : ""}
            {` · ${job.guarantee_days} day guarantee`}
          </Text>
        </Stack>

        <div className="jobCardEarn">
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)" mb="var(--axio-unit-4)">
            You earn on hire
          </Text>
          <Text className="jobCardEarnAmount">{sourcingLabel(job)}</Text>
          <Text size="xs" c="dimmed" mt="var(--axio-unit-6)" className="tabular">
            {job.sourcing_fee_pct != null
              ? `${job.sourcing_fee_pct}% sourcing share`
              : "Sourcing % set at publish"}
            {" · "}
            Placement fee{" "}
            {job.fee_basis === "flat"
              ? formatPaise(job.fee_flat_paise)
              : `${job.fee_pct_ctc ?? "—"}% of CTC`}
          </Text>
          {share && share.min !== share.max && (
            <Text size="xs" c="dimmed" mt="var(--axio-unit-2)" className="tabular">
              Based on CTC {formatPaise(job.ctc_min_paise)} – {formatPaise(job.ctc_max_paise)}
            </Text>
          )}
        </div>

        <Group justify="space-between" mt="auto">
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            Role CTC
          </Text>
          <Text size="sm" fw="var(--axio-font-weight-semibold)" className="tabular">
            {formatPaise(job.ctc_min_paise)} – {formatPaise(job.ctc_max_paise)}
          </Text>
        </Group>
      </Stack>
    </ContentCard>
  );
}

const AFFILIATE_JOB_FILTER_DEFAULTS = {
  segment: "",
  city: "",
};

export function AffiliateJobsPage() {
  const jobFilters = useQueryFilters(AFFILIATE_JOB_FILTER_DEFAULTS);
  const segment = jobFilters.draft.segment;
  const city = jobFilters.draft.city;
  const { flatOptions: segmentOptions } = usePublicSegmentCatalog();

  const jobsQuery = useInfinitePage(["affiliate-jobs", jobFilters.values.segment, jobFilters.values.city], (page, pageSize) =>
    jobsApi.list({
      segment: (jobFilters.values.segment as Segment) || undefined,
      city: jobFilters.values.city || undefined,
      page,
      page_size: pageSize,
    }),
  );

  const jobs = jobsQuery.items;

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.openJobs}
        title="Open jobs — your sourcing payout"
        subtitle="See what you can earn when a candidate you referred gets hired. Share your link and push these roles hard."
      />

      <FilterBar>
        <Select
          placeholder="All segments"
          data={segmentOptions}
          value={segment || null}
          onChange={(value) => jobFilters.set({ segment: value ?? "" })}
          clearable
          searchable
          renderOption={renderSegmentOption}
          filter={catalogFuzzyFilter}
          limit={40}
          nothingFoundMessage="No matching segments"
          w="var(--axio-unit-220)"
        />
        <CitySelect
          label=""
          placeholder="All cities"
          flat
          publicCatalog
          value={city}
          onChange={(value) => jobFilters.set({ city: value })}
          w="var(--axio-unit-260)"
        />
      </FilterBar>

      {jobsQuery.isLoading ? (
        <LoadingOverlay />
      ) : jobsQuery.isError ? (
        <EmptyState
          icon={NAV_ICONS.openJobs}
          title="Could not load jobs"
          description={
            jobsQuery.error instanceof Error ? jobsQuery.error.message : "Something went wrong."
          }
        />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={NAV_ICONS.openJobs}
          title="No open jobs match your filters"
          description="Try adjusting segment or city filters to see available roles."
        />
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {jobs.map((job) => (
              <AffiliateJobCard key={job.id} job={job} />
            ))}
          </SimpleGrid>
          <ListScrollSentinel
            hasMore={jobsQuery.hasMore}
            loadingMore={jobsQuery.loadingMore}
            onLoadMore={jobsQuery.loadMore}
          />
        </>
      )}
    </Stack>
  );
}
